package com.appsyde.shared.presentation.screen.screen

import androidx.compose.runtime.Composable
import com.appsyde.shared.presentation.style.AppStylesLanding
import org.jetbrains.compose.web.css.Style
import org.jetbrains.compose.web.dom.*

@Composable
fun PrivacyPage() {
    Style(AppStylesLanding)
    LegalPageLayout(
        title = "Privacy Policy",
        content = {
            P({ classes(AppStylesLanding.legalUpdated) }) { Text("Last updated: March 10, 2026") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("AppSyde (\"we\", \"our\", or \"us\") respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, store, and protect information when you use the AppSyde service.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("AppSyde is a support assistant designed to help teams manage and respond to customer support emails more efficiently.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("By using AppSyde, you agree to the practices described in this Privacy Policy.")
            }
            Div({ classes(AppStylesLanding.divider) })

            H2({ classes(AppStylesLanding.legalH2) }) { Text("1. Information We Collect") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("We collect only the information necessary to provide and operate the AppSyde service.")
            }
            H3({ classes(AppStylesLanding.legalH3) }) { Text("Account Information") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("When you create an account, we may collect:")
            }
            Ul({ classes(AppStylesLanding.legalList) }) {
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("Name") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("Email address") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("Workspace information") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("Authentication data via Google OAuth") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("Account configuration settings") }
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("This information is required to operate your AppSyde workspace.")
            }
            Div({ classes(AppStylesLanding.divider) })

            H2({ classes(AppStylesLanding.legalH2) }) { Text("2. How We Use Information") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("We use collected information only for legitimate service purposes, including:")
            }
            Ul({ classes(AppStylesLanding.legalList) }) {
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("providing the AppSyde support assistant") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("generating AI-assisted draft replies") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("organizing and displaying support conversations") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("improving the accuracy of support suggestions") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("maintaining system reliability and security") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("processing payments and managing subscriptions") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("providing customer support") }
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("AppSyde does ")
                B { Text("not sell personal data") }
                Text(".")
            }
            Div({ classes(AppStylesLanding.divider) })

            H2({ classes(AppStylesLanding.legalH2) }) { Text("3. AI Processing") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("AppSyde uses artificial intelligence models to assist agents in drafting support replies.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("AI processing may involve analyzing:")
            }
            Ul({ classes(AppStylesLanding.legalList) }) {
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("email messages") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("conversation context") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("structured support information extracted from tickets") }
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("AI-generated responses are ")
                B { Text("always suggestions only") }
                Text(".")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("All responses must be reviewed and validated by a human agent before being sent.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("AppSyde does not automatically send responses to customers.")
            }
            Div({ classes(AppStylesLanding.divider) })

            H2({ classes(AppStylesLanding.legalH2) }) { Text("4. Gmail Integration") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("AppSyde integrates with Gmail using Google OAuth in order to provide support email management and assistance features.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("By connecting your Gmail account, you authorize AppSyde to access certain email data required to operate the Service. This may include reading email messages, analyzing conversation context, and allowing users to send replies from within the platform.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("AppSyde accesses Gmail data only for the purpose of providing the Service's support assistance features.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("AppSyde does not use Gmail data for advertising, profiling, or resale.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("Users may revoke AppSyde's access to their Gmail account at any time through their Google Account permissions settings.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("AppSyde's use and transfer of information received from Google APIs will adhere to the Google API Services User Data Policy, including the Limited Use requirements.")
            }
            Div({ classes(AppStylesLanding.divider) })

            H2({ classes(AppStylesLanding.legalH2) }) { Text("5. Data Sharing") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("We may share limited data with trusted service providers strictly required to operate the AppSyde platform.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("These providers may include:")
            }
            Ul({ classes(AppStylesLanding.legalList) }) {
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("cloud hosting infrastructure providers") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("AI model providers used to generate suggestions") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("payment processors such as Stripe") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("security and monitoring services") }
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("These providers are contractually obligated to process data only for the purpose of delivering their services to AppSyde.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("We do not sell, rent, or trade personal data.")
            }
            Div({ classes(AppStylesLanding.divider) })

            H2({ classes(AppStylesLanding.legalH2) }) { Text("6. Data Storage and Security") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("We implement appropriate technical and organizational measures to protect user data.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("These measures include:")
            }
            Ul({ classes(AppStylesLanding.legalList) }) {
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("encrypted data transmission (HTTPS)") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("secure authentication mechanisms") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("restricted internal access to sensitive data") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("infrastructure security best practices") }
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("While we take reasonable steps to protect your information, no system can guarantee absolute security.")
            }
            Div({ classes(AppStylesLanding.divider) })

            H2({ classes(AppStylesLanding.legalH2) }) { Text("7. Data Retention") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("We retain data only for as long as necessary to provide the AppSyde service and comply with legal obligations.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("Support conversation data may be retained while your account remains active.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("If you delete your account, we will delete or anonymize your data within a reasonable period unless retention is required by law.")
            }
            Div({ classes(AppStylesLanding.divider) })

            H2({ classes(AppStylesLanding.legalH2) }) { Text("8. User Rights (GDPR)") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("If you are located in the European Economic Area (EEA), you have certain rights under the General Data Protection Regulation (GDPR).")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("These rights include:")
            }
            Ul({ classes(AppStylesLanding.legalList) }) {
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("the right to access your personal data") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("the right to request correction of inaccurate data") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("the right to request deletion of your data") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("the right to restrict processing") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("the right to data portability") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("the right to object to certain processing activities") }
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("Requests can be submitted using the contact information below.")
            }
            Div({ classes(AppStylesLanding.divider) })

            H2({ classes(AppStylesLanding.legalH2) }) { Text("9. Cookies and Session Data") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("AppSyde may use cookies or similar technologies to:")
            }
            Ul({ classes(AppStylesLanding.legalList) }) {
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("maintain authenticated sessions") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("ensure platform security") }
                Li({ classes(AppStylesLanding.legalListItem, AppStylesLanding.cardBody) }) { Text("improve user experience") }
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("These cookies are essential for the operation of the service.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("AppSyde does not use tracking cookies for advertising purposes.")
            }
            Div({ classes(AppStylesLanding.divider) })

            H2({ classes(AppStylesLanding.legalH2) }) { Text("10. Changes to This Privacy Policy") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("We may update this Privacy Policy from time to time to reflect product changes, legal requirements, or operational improvements.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("When we update the policy, we will revise the \"Last updated\" date at the top of this page.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("Continued use of AppSyde after changes indicates acceptance of the updated policy.")
            }
            Div({ classes(AppStylesLanding.divider) })

            H2({ classes(AppStylesLanding.legalH2) }) { Text("11. Data Security") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("AppSyde implements reasonable technical and organizational security measures designed to protect user data from unauthorized access, disclosure, alteration, or destruction.")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("While no system can guarantee absolute security, AppSyde takes appropriate steps to safeguard the information processed through the Service.")
            }
            Div({ classes(AppStylesLanding.divider) })

            H2({ classes(AppStylesLanding.legalH2) }) { Text("12. Contact") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("If you have questions regarding this Privacy Policy or your personal data, you may contact us at:")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("Email: ")
                A("mailto:appsyde.tb.help@gmail.com", { classes(AppStylesLanding.navLink) }) { Text("appsyde.tb.help@gmail.com") }
            }
            Div({ classes(AppStylesLanding.divider) })

            H2({ classes(AppStylesLanding.legalH2) }) { Text("13. Company Information") }
            P({ classes(AppStylesLanding.cardBody) }) {
                Text("AppSyde is operated by:")
            }
            P({ classes(AppStylesLanding.cardBody) }) {
                B { Text("AppSyde") }
                Br()
                Text("France")
            }
        }
    )
}
